import { useContext } from 'react'

import { UserContext } from '@editor/contexts/Users'
import { InspectionItemType } from '@editor/interfaces/inspection'
import { useBlueprintEditorContext } from '@editor/pages/projects/blueprint-editor/contexts/BlueprintEditorContext'
import { Mode } from '@editor/pages/projects/blueprint-editor/definitions'
import useConfigureCanvas from '@editor/pages/projects/blueprint-editor/hooks/useConfigureCanvas'
import {
  deleteBlueprint,
  resetCreateBlueprint,
  setIsCreatingBlueprint,
  setMode,
  setSelectedBlueprint,
} from '@editor/pages/projects/blueprint-editor/store/blueprintEditor'
import { setDrawings, setEditorElements } from '@editor/pages/projects/blueprint-editor/store/drawings'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { resetWorking } from '@editor/pages/projects/editor/tools/MaskPCD/store'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'

const useCreateBlueprint = () => {
  // Contexts
  const editorContext = useBlueprintEditorContext()
  const { getAccessToken } = useContext(UserContext)

  // Hooks
  const { setupControls, orientCamera } = useConfigureCanvas(editorContext)

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const blueprints = useSelector((state: RootState) => state.blueprint.editor.blueprints)

  /**
   * Handle starting the creation of a new blueprint
   */
  const handleStartCreatingBlueprint = () => {
    if (!project || !inspectionArea) return

    dispatch(setMode(Mode.Editor3D))
    setupControls(Mode.Editor3D)
    orientCamera(Mode.Editor3D)

    // Set a dummy blueprint to trigger creation
    dispatch(
      setSelectedBlueprint({
        sort_key: '',
        project_id: project.project_id,
        inspection_area_id: inspectionArea.inspection_area_id,
        blueprint_id: crypto.randomUUID(),
        name: `設計図面${blueprints.length + 1}`,
      }),
    )
    dispatch(setIsCreatingBlueprint(true))

    // Set all editor elements as checked.
    dispatch(
      setEditorElements({
        inspectionItems: [
          ...inspectionItems
            .filter((item) => [InspectionItemType.Volume, InspectionItemType.PolylineLength].includes(item.item_type!))
            .map(({ inspection_item_id }) => ({
              id: inspection_item_id!,
              order: 0,
            })),
        ],
        polygons: [
          ...inspectionItems
            .filter((item) => [InspectionItemType.PolygonArea].includes(item.item_type!) && !item.volume_id)
            .map(({ shape_ids }) => ({
              id: shape_ids.polygons[0],
              order: 0,
            })),
        ],
        cylinders: [],
      }),
    )
    dispatch(setDrawings([]))
  }

  /**
   * Handle blueprint deletion
   *
   * @param blueprintId - The ID of the blueprint to delete
   */
  const handleBlueprintDelete = async (blueprintId: string) => {
    if (!project || !inspectionArea) return

    const token = await getAccessToken()
    if (!token) return

    await dispatch(
      deleteBlueprint({
        accessToken: token,
        projectId: project.project_id,
        areaId: inspectionArea.inspection_area_id,
        blueprintId,
      }),
    )
  }

  /**
   * Handle canceling the creation of a blueprint
   */
  const handleCancelCreatingBlueprint = () => {
    dispatch(resetCreateBlueprint())
    dispatch(resetWorking())

    // Return to 2D mode after canceling blueprint creation
    dispatch(setMode(Mode.Editor2D))
    setupControls(Mode.Editor2D)
    orientCamera(Mode.Editor2D)
    dispatch(setAttentionText({ message: '' }))
  }

  return {
    handleStartCreatingBlueprint,
    handleBlueprintDelete,
    handleCancelCreatingBlueprint,
  }
}

export default useCreateBlueprint
